import { stringify } from 'qs';
import React from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { DefaultLayout } from '@layouts';
import {
  Hero,
  Highlights,
  Contacts,
  Offerings,
  Divider,
  MediaDownloadBlock,
  TheFork,
} from '@components';
import dayjs from 'dayjs';

const StoreDetail = ({ store }) => {
  const {
    slug,
    name,
    hero,
    offerings,
    contacts,
    coordinates,
    banner,
    media_download_block,
    highlights,
    the_fork_button,
    seo,
  } = store;
  const today = dayjs();
  const targetDate = dayjs('2025-07-31');
  const closingPeriod =
    today.isBefore(targetDate) || today.isSame(targetDate);

  const hideBooking =
    slug === 'ristorante-momento-neapolis' ||
    (closingPeriod && slug === 'ristorante-degli-scavi');
  return (
    <>
      <NextSeo
        title={seo?.metaTitle || `Momento | ${name}`}
        description={seo?.metaDescription || hero?.description}
        additionalMetaTags={
          seo?.keywords
            ? [
                {
                  name: 'keywords',
                  content: seo?.keywords,
                },
              ]
            : []
        }
        canonical={seo?.canonicalURL}
      />

      <DefaultLayout
        showBookingCta={!hideBooking}
        ctaUrl={`/booking?store=${slug}`}
        header={
          <Hero
            isHero
            label={hero?.label}
            title={name}
            description={hero?.description}
            hasSlider={hero?.media?.data?.length > 0}
            slides={hero?.media?.data?.map(({ attributes }) => {
              return {
                img: attributes,
              };
            })}
            cta={
              the_fork_button && (
                <div className="mt-4">
                  <TheFork link={the_fork_button} />
                </div>
              )
            }
          />
        }
      >
        <div className="pt-16 sm:pt-0"></div>
        {highlights?.length > 0 && (
          <Highlights header={null} slides={highlights} />
        )}

        <Contacts
          contacts={contacts?.list}
          header={contacts?.header}
          coordinates={coordinates}
        />
        <Offerings
          header={offerings.header}
          offerings={offerings?.offerings?.data}
        />
        {banner && <Divider cta={null} {...banner} />}
        {media_download_block?.gallery && (
          <MediaDownloadBlock
            header={media_download_block?.header}
            gallery={media_download_block?.gallery}
            cta={media_download_block?.cta}
          />
        )}
      </DefaultLayout>
    </>
  );
};

export default StoreDetail;
export async function getServerSideProps({ params, locale }) {
  const path = process.env.NEXT_PUBLIC_STRAPI; // process.env.NEXT_PUBLIC_STRAPI;

  const qs = stringify(
    {
      populate: [
        'hero',
        'hero.media',
        'highlights.list',
        'highlights.img',
        'highlights.cta',
        'city',
        'contacts.header',
        'contacts.list',
        'offerings.header',
        'offerings.offerings',
        'coordinates',
        'banner',
        'banner.img',
        'media_download_block.gallery',
        'media_download_block.header',
        'media_download_block.cta',
        'media_download_block.cta.file',
        'seo',
        'type',
      ],
      filters: {
        slug: {
          $eq: params?.slug,
        },
      },
      locale: locale,
    },
    {
      encodeValuesOnly: true, // prettify URL
    },
  );
  const getProdData = await fetch(`${path}/stores?${qs}`);
  const product = await getProdData.json();

  if (product.data?.[0]) {
    return {
      props: {
        store: product.data?.[0]?.attributes,
        ...(await serverSideTranslations(locale, ['common'])),
        path: `${path}/stores?${qs}`,
      },
    };
  }

  return {
    redirect: {
      permanent: true,
      destination: '/',
    },
  };
}
